<template>
	<div>
		<!-- Submenu -->
		<quote-detail-submenu />

		<!-- Body -->
		<core-layout-content large>
			<!-- Content -->
			<div class="flex wrap xs-12">
				<!-- Left -->
				<div class="xs-12 md-7 md-pr4">
					<!-- Particulars -->
					<quote-particulars
						:particulars="fetchedQuote"
						class="mt6 md-mt0"
						@updated="fetchQuote"
					/>

					<!-- Accepted Offer -->
					<j-card class="bg5 mt6">
						<!-- Headline -->
						<div class="p2 pt4 pb4 bb1 bc7 bg5">
							<div class="fs5 c1 fw7 lh125 mb1">{{ $t("accepted_offer") }}</div>
							<div class="fs3 c6 lh125">
								{{ $t("accepted_offer_description") }}
							</div>
						</div>
						<!-- Body -->
						<!-- If there is accepted quote or pricing or product on quote, show things -->
						<!-- Else show empty state -->
						<div
							class="p4"
							v-if="fetchedQuote.acceptedOffer || fetchedQuote.premium"
						>
							<!-- Product -->
							<product-info :product="computedProduct" />

							<!-- Premium with discounts and loadings -->
							<premium-info
								v-if="computedAcceptedOfferId"
								:policyId="fetchedQuote.id"
								:pricing="computedPricing"
								:productId="computedProduct ? computedProduct.productId : {}"
								:offerId="computedAcceptedOfferId"
								:hasLoadingsActions="true"
								:hasDiscountActions="true"
								:hasPremiumActions="true"
								@loading:created="fetchQuote"
								@loading:deleted="fetchQuote"
								@discount:created="fetchQuote"
								@discount:deleted="fetchQuote"
								@premium:updated="fetchQuote"
								class="mt6"
							/>

							<!-- Exclusions -->
							<exclusions-info
								:policyId="fetchedQuote.id"
								:productId="computedProduct ? computedProduct.productId : {}"
								:list="computedExclusions"
								@deleted="fetchQuote"
								@created="fetchQuote"
								class="mt6"
							/>

							<!-- Benefit Options -->
							<quote-benefit-options
								:list="computedBenefitOptions"
								class="mt6"
							/>
						</div>

						<!-- Empty state -->
						<div class="fs3 c1 ta2 p4" v-else>
							{{ this.$t("errors.no_data") }}
						</div>
					</j-card>

					<!-- Holder -->
					<main-holder
						:holder="computedHolder"
						class="mt6"
						@afterChangeHolder="fetchQuote"
					/>

					<!-- Other Holders -->
					<other-holders
						:list="computedOtherHolders"
						class="mt6"
						@afterAddHolder="fetchQuote"
						@afterRemoveHolder="fetchQuote"
					/>

					<!-- Insured People -->
					<insured-people
						:list="computedInsuredPeople"
						class="mt6"
						@afterAddInsured="fetchQuote"
						@afterRemoveInsured="fetchQuote"
					></insured-people>

					<!-- Insured Objects -->
					<insured-objects
						:list="computedInsuredObjects"
						class="mt6"
						@afterAddInsured="fetchQuote"
						@afterRemoveInsured="fetchQuote"
					></insured-objects>

					<!-- Payment Info -->
					<payment-infos
						:list="computedPaymentInfo"
						class="mt6"
						@removed="fetchQuote"
						@added="fetchQuote"
						@updated="fetchQuote"
					/>
				</div>

				<!-- Right -->
				<div class="xs-12 md-5 md-pl4">
					<!-- Info Widget -->
					<core-info-card :data="computedInfo" class="mt6 md-mt0" />

					<!-- Quote actions -->
					<quote-actions
						v-if="$store.state.settings.quotes.actions.hasActions"
						v-can="'writeQuotes'"
						class="mt6"
						:acceptedOffer="fetchedQuote.acceptedOffer"
						:quoteId="fetchedQuote.id"
						:amount="computedPricing.amount"
						:currencyCode="computedPricing.currencyCode"
						:context="fetchedQuote"
					/>

					<!-- Rejection Information - only showed when quote is rejected -->
					<reject-info
						v-if="fetchedQuote.status === 'REJECTED'"
						:codes="fetchedQuote.rejectionCodes"
						:remarks="fetchedQuote.rejectionRemarks"
						class="mt6"
					/>

					<!-- Notes -->
					<core-notes-card
						notesOf="policy"
						:id="fetchedQuote.id"
						:list="computedNotes"
						:canWrite="canWriteQuoteNotes"
						@note:created="fetchQuote"
						@note:updated="fetchQuote"
						@note:deleted="fetchQuote"
						class="mt6"
					/>

					<!-- Files -->
					<core-files-card
						:id="fetchedQuote.id"
						filesOf="policy"
						:list="computedAttachments"
						:canWrite="canWriteFiles"
						@file:deleted="fetchQuote"
						@files:uploaded="fetchQuote"
						class="mt6"
					/>
				</div>
			</div>

			<!-- Footer menu -->
			<div
				class="flex wrap xs-12 xs-right bt1 bc7 pt2 mt8"
				v-can="'writeQuotes'"
			>
				<!-- Reject Quote -->
				<j-button
					v-if="$store.state.settings.quotes.detail.hasReject"
					type="secondary"
					@click="$modal.show('rejectQuote')"
					action="showRejectQuoteModal"
				>
					{{ this.$t("reject_quote") }}
				</j-button>

				<!-- Delete -->
				<j-button
					type="error"
					@click="$modal.show('j-dialog')"
					v-can:or="['writeQuotes']"
					class="ml2"
					action="showDeleteQuoteModal"
				>
					<crm-icon name="delete" size="normal" color="c5" class="mr1" />
					{{ $t("delete_quote") }}
				</j-button>
			</div>

			<!-- Reject Quote Modal -->
			<core-modal name="rejectQuote" :headline="$t('reject_quote')">
				<reject-quote
					:payload="fetchedQuote"
					:id="id"
					@rejected="afterRejection"
				/>
			</core-modal>

			<!-- Delete Confirmation Modal -->
			<j-dialog
				:title="$t('delete_quote')"
				type="delete"
				@confirm="onDelete(id)"
				>{{ $t("errors.are_you_sure_you_want_to_delete_quote") }}</j-dialog
			>
		</core-layout-content>

		<!-- Loading toast -->
		<j-loading-toast v-if="isLoading" />
	</div>
</template>

<script>
import QuoteDetailSubmenu from "../components/QuoteDetailSubmenu"
import JButton from "@/components/JButton/"
import JCard from "@/components/JCard/"
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent"
import CoreModal from "@/modules/core/components/CoreModal"
import MainHolder from "../components/MainHolder"
import OtherHolders from "../components/OtherHolders"
import InsuredPeople from "../components/InsuredPeople"
import InsuredObjects from "../components/InsuredObjects"
import RejectQuote from "../components/RejectQuote"
import RejectInfo from "../components/RejectInfo"
import PaymentInfos from "../components/PaymentInfos"
import CoreNotesCard from "@/modules/core/components/CoreNotesCard/index.vue"
import JLoadingToast from "@/components/JLoadingToast"
import QuoteParticulars from "../components/QuoteParticulars"
import CoreFilesCard from "@/modules/core/components/CoreFilesCard"
import CoreInfoCard from "@/modules/core/components/CoreInfoCard"
import ProductInfo from "../components/ProductInfo"
import PremiumInfo from "../components/PremiumInfo"
import ExclusionsInfo from "../components/ExclusionsInfo"
import QuoteActions from "../components/QuoteActions"
import QuoteBenefitOptions from "../components/QuoteBenefitOptions"
import api from "../api/"
import get from "lodash.get"
import JDialog from "@/components/JDialog/"
import CrmIcon from "@/components/CrmIcon/"
import { can } from "@/permissions"

export default {
	components: {
		QuoteDetailSubmenu,
		JButton,
		JCard,
		CoreLayoutContent,
		CoreModal,
		OtherHolders,
		InsuredPeople,
		InsuredObjects,
		MainHolder,
		RejectQuote,
		RejectInfo,
		PaymentInfos,
		CoreNotesCard,
		JLoadingToast,
		QuoteParticulars,
		CoreFilesCard,
		CoreInfoCard,
		ProductInfo,
		PremiumInfo,
		ExclusionsInfo,
		QuoteActions,
		QuoteBenefitOptions,
		JDialog,
		CrmIcon
	},
	props: {
		id: { type: String, required: true }
	},
	data() {
		return {
			isLoading: false,
			fetchedQuote: {}
		}
	},
	computed: {
		computedAcceptedOfferId() {
			return this.fetchedQuote.acceptedOffer?.id || this.fetchedQuote.id
		},
		computedHolder() {
			return this.fetchedQuote.holder ? this.fetchedQuote.holder : {}
		},
		computedOffers() {
			return this.fetchedQuote.offers ? this.fetchedQuote.offers : []
		},
		computedOtherHolders() {
			return this.fetchedQuote.otherHolders
				? this.fetchedQuote.otherHolders
				: []
		},
		computedInfo() {
			return {
				status: this.fetchedQuote.status,
				createdAt: this.fetchedQuote.createdAt,
				createdBy: this.fetchedQuote.createdBy,
				lastModifiedAt: this.fetchedQuote.lastModifiedAt,
				lastModifiedBy: this.fetchedQuote.lastModifiedBy
			}
		},
		computedNotes() {
			return this.fetchedQuote.notes ? this.fetchedQuote.notes : []
		},
		computedExclusions() {
			return this.fetchedQuote.acceptedOffer
				? this.fetchedQuote.acceptedOffer.exclusions
				: this.fetchedQuote.exclusions
		},
		computedProduct() {
			return this.fetchedQuote.acceptedOffer
				? this.fetchedQuote.acceptedOffer.product
				: this.fetchedQuote.product
		},
		computedPricing() {
			return this.fetchedQuote.acceptedOffer
				? this.fetchedQuote.acceptedOffer.premium
				: this.fetchedQuote.premium
		},
		computedPaymentInfo() {
			return this.fetchedQuote.paymentInfos
				? this.fetchedQuote.paymentInfos
				: []
		},
		computedAttachments() {
			return this.fetchedQuote.attachments ? this.fetchedQuote.attachments : []
		},
		computedInsuredPeople() {
			const insureds = get(this, "fetchedQuote.insured")
			if (!insureds) return []
			// Filter out only insured people, ignore objects
			return insureds.filter(item => item.__typename === "individual")
		},
		computedInsuredObjects() {
			const insureds = get(this, "fetchedQuote.insured")
			if (!insureds) return []
			// Filter out only insured objects, ignore individuals
			return insureds.filter(item => item.__typename === "object")
		},
		computedBenefitOptions() {
			const benefitOptions =
				get(this, "fetchedQuote.acceptedOffer.benefitOptions") ||
				get(this, "fetchedQuote.benefitOptions")
			if (!benefitOptions) return []
			return benefitOptions
		},
		canWriteQuoteNotes() {
			return can("writePolicyNotes")
		},
		canWriteFiles() {
			return can("writeFiles")
		}
	},
	methods: {
		async fetchQuote() {
			this.isLoading = true
			const res = await api.quote({
				where: { id: this.id }
			})
			this.isLoading = false
			// Check errors
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
				return
			}
			// If it doesn't exist, redirect
			if (res.data.policies.list.length === 0)
				this.$router.push({ name: "QuotesList" })
			// Assign
			this.fetchedQuote = res.data.policies.list[0]
		},
		afterRejection() {
			this.fetchQuote()
			this.$modal.hide("rejectQuote")
		},
		async onDelete(id) {
			this.isLoading = true
			const res = await api.deleteQuote({ id })
			this.isLoading = false
			// Error
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
			}
			// go back to previous page
			if (res) this.$router.push(`/${this.$store.state.tenantId}/quotes/`)
			this.$store.dispatch("addMessage", {
				message: {
					type: "success",
					text: this.$t("quote_deleted")
				}
			})
		}
	},
	created() {
		// Check if we are receiving all props
		// If not, redirect to products listing
		if (!this.id) {
			this.$router.push({ name: "QuotesList" })
			return
		}
		// Fetch product info
		this.fetchQuote()
	}
}
</script>
